import * as RoleTypes from "@wesstron/utils/Api/constants/roleTypes";
import React from "react";
import {connect} from "react-redux";
import i18n from "../i18n";
import {makeGetManageBuildingsList} from "../selectors/buildingsSelector";
import {getLicenses} from "../selectors/farmSelector";
import {checkIfUserIsMasterService} from "../utils/NewRolesUtils";
import {userIsService} from "../utils/NewRolesUtils2";
import {displayComponent} from "../utils/WithRolesUtils";
import InfoBox from "./basics/info-box/InfoBox";
import NoRoleComponent from "./NoRoleComponent";

const getManageBuildingsList = makeGetManageBuildingsList();

function makeMapStateToProps() {
    return function mapStateToProps(state) {
        return {
            farm: state.location.farm,
            user: state.user.user,
            _buildingsMap: getManageBuildingsList(state),
            licenses: getLicenses(state),
        };
    };
}

/**
 * HOC do wrapowania komponentu (tak jak reduxForm albo connect) do sprawdzania czy użytkownik
 * ma potrzebne role do wyświetlenia komponentu
 * @param props {object|function}               obiekt z danymi, moze byc funkcja, ktora zwraca obiekt
 *                                              (przydatne przy wyciaganiu DevID, poniewaz parametrem sa propsy komponentu)
 *
 * @property props.roles {array}                lista ról minimalnych dla widoku
 *
 * @property props.devID {string}               DevID urządzenia dla roli
 *
 * @property props.showComponent {boolean}      określenie czy ma pokazać komponent, jeżeli ktoś nie ma
 *                                              uprawnień do wejścia
 * @property props.requiredLicenses {Object}    Informacje, które rzeczy są wymagane. Dozwolone klucze: and, or. Wartości to tablice z licencjami. Przykład:
 *                                              {
 *                                                  and: ["breeding.sow", "utilities.solidmap"]
 *                                              }.- oznacza, że wymagane są obie licencje
 *                                              {
 *                                                  or: ["breeding.sow", "utilities.solidmap"]
 *                                              } - oznacza, że wymagana jest przynajmniej jedna z licencji
 * @return {function(*): WithRoles}             komponent
 */
const withRoles = (props) => (WrappedComponent) => {
    class WithRoles extends React.Component {
        constructor(props) {
            super(props);
            const {user, farm} = this.props;
            this.isService = userIsService(user, farm);
            this.isMasterService = checkIfUserIsMasterService();
            this.p = this.getProps();
            this.state = {
                show: false,
                showMasterView: false,
            };
        }

        componentDidMount() {
            this.setState({
                show: this.checkIfCanBeDisplayed(),
                showMasterView: this.checkIfCanShowMasterComponent(),
            });
        }

        // pobranie propsow
        getProps() {
            if (typeof props === "function") return props(this.props);
            return props;
        }

        checkIfCanShowMasterComponent() {
            const {roles = []} = this.p;
            let isMasterServiceRequired = roles.includes(
                RoleTypes.MASTER_SERVICE
            );
            return !(isMasterServiceRequired && !this.isMasterService);
        }

        checkIfCanBeDisplayed() {
            const {roles = [], devPlcmnts, hideFor, requiredLicenses} = this.p;
            const {farm, user, _buildingsMap, licenses} = this.props;
            return displayComponent(
                roles,
                devPlcmnts,
                farm,
                user,
                _buildingsMap,
                this.isService,
                licenses,
                requiredLicenses,
                hideFor
            );
        }

        render() {
            const {show, showMasterView} = this.state;
            const {showComponent, showInfo, showServiceWarning} = this.p;
            if (!showMasterView) return null;
            if (!show) {
                if (showComponent) return <NoRoleComponent />;
                if (showInfo)
                    return (
                        <InfoBox boxColor="warning">
                            {i18n.t("withRoles.noAccessWarningMessage")}
                        </InfoBox>
                    );
                return null;
            }
            return (
                <>
                    {showServiceWarning && this.isService && !show && (
                        <InfoBox boxColor="warning" className="max-height-2rem">
                            {i18n.t("withRoles.serviceViewMessage")}
                        </InfoBox>
                    )}
                    <WrappedComponent {...this.props} />
                </>
            );
        }
    }

    WithRoles.displayName = `WithRoles(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

    return connect(makeMapStateToProps)(WithRoles);
};

export default withRoles;
